import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Loader, createUseStyles } from '@Sage/hoi-poi-ui';

import usePdfDocument, { DEFAULT_PDF_DOCUMENT_OPTIONS } from 'hooks/usePdfDocument';
import usePdfPageRender from 'hooks/usePdfPageRender';
import { useWeb5Context } from 'lib/Web5Context';
import fscreen from 'utils/fullScreen';

import Toolbar from './Toolbar';
import styles from './styles';

const useStyles = createUseStyles(styles, 'PdfViewer');

const PdfViewer = memo(
    ({
        currentPage,
        documentOptions,
        externalRef,
        fileUrl,
        initialScale = 1,
        onError,
        onFullscreenEnter,
        onViewerStateChange,
        renderOverlay,
        toolbarExtra,
    }) => {
        const { t } = useWeb5Context();
        const classes = useStyles();

        const [page, setPage] = useState(() => currentPage || 1);
        const [scale, setScale] = useState({
            current: initialScale || 1,
            min: 0.5,
            max: 2,
            delta: 0.25,
        });
        const [fullscreen, setFullscreen] = useState(false);

        const pageRef = useRef(null);
        const canvasRef = useRef(null);
        const viewerRef = useRef(null);

        const mergedDocumentOptions = useMemo(
            () => ({
                ...DEFAULT_PDF_DOCUMENT_OPTIONS,
                ...documentOptions,
            }),
            [documentOptions],
        );

        const { pdf, numPages, isLoading, error } = usePdfDocument(fileUrl, mergedDocumentOptions);
        const { isRendering, renderError, renderGeneration } = usePdfPageRender({
            pdf,
            pageNumber: page,
            scale: scale.current,
            canvasRef,
        });

        useEffect(() => {
            if (error) {
                onError?.(error);
            }
        }, [error, onError]);

        useEffect(() => {
            if (renderError) {
                onError?.(renderError);
            }
        }, [onError, renderError]);

        useEffect(() => {
            onViewerStateChange?.({
                page,
                numPages,
                scale: scale.current,
                size: {
                    width: pageRef.current?.clientWidth,
                    height: pageRef.current?.clientHeight,
                },
                getSize: () => ({
                    width: pageRef.current?.clientWidth,
                    height: pageRef.current?.clientHeight,
                }),
            });
        }, [numPages, onViewerStateChange, page, renderGeneration, scale, isRendering]);

        const handleFullscreenChange = useCallback((event) => {
            const el = event.target;
            const isFullscreen = fscreen.fullscreenElement === el;
            setFullscreen(isFullscreen);
        }, []);

        const handleFullscreen = useCallback(() => {
            const el = externalRef || viewerRef.current;

            if (!el) {
                return;
            }

            el.onfullscreenchange = handleFullscreenChange;

            if (!fscreen.fullscreenElement) {
                fscreen.requestFullscreen(el);
                onFullscreenEnter?.();
            } else {
                fscreen.exitFullscreen();
            }
        }, [externalRef, handleFullscreenChange, onFullscreenEnter]);

        const handleZoomIn = useCallback(
            () =>
                setScale(({ current, min, delta }) => ({
                    ...scale,
                    current: current > min ? current - delta : current,
                })),
            [scale],
        );

        const handleZoomOut = useCallback(
            () =>
                setScale(({ current, max, delta }) => ({
                    ...scale,
                    current: current < max ? current + delta : current,
                })),
            [scale],
        );

        const handleGoToPrevPage = useCallback(
            () =>
                setPage((current) => {
                    return current > 1 ? current - 1 : current;
                }),
            [],
        );

        const handleGoToNextPage = useCallback(
            () =>
                setPage((current) => {
                    return current < numPages ? current + 1 : current;
                }),
            [numPages],
        );

        const showLoader = isLoading || isRendering || (!pdf && fileUrl);

        return (
            <div className={classes.root} ref={viewerRef}>
                {fullscreen && (
                    <Button className={classes.exitFullscreen} onClick={handleFullscreen}>
                        {t('action_exit_fullscreen')}
                    </Button>
                )}
                <div className={classes.frame}>
                    <div className={classes.document}>
                        {showLoader && (
                            <div className={classes.loader}>
                                <Loader size="big" />
                            </div>
                        )}
                        {pdf && (
                            <div className={classes.wrap}>
                                {renderOverlay?.({ page, numPages, scale: scale.current })}
                                <div className={classes.page} ref={pageRef}>
                                    <canvas ref={canvasRef} className={classes.canvas} />
                                </div>
                            </div>
                        )}
                    </div>
                    <Toolbar
                        fullscreen={fullscreen}
                        goToNextPage={handleGoToNextPage}
                        goToPrevPage={handleGoToPrevPage}
                        numPages={numPages}
                        page={page}
                        scale={scale}
                        toggleFullscreen={handleFullscreen}
                        toolbarExtra={toolbarExtra}
                        zoomIn={handleZoomIn}
                        zoomOut={handleZoomOut}
                    />
                </div>
            </div>
        );
    },
);

PdfViewer.propTypes = {
    currentPage: PropTypes.number,
    documentOptions: PropTypes.object,
    externalRef: PropTypes.object,
    fileUrl: PropTypes.string,
    initialScale: PropTypes.number,
    onError: PropTypes.func,
    onFullscreenEnter: PropTypes.func,
    onViewerStateChange: PropTypes.func,
    renderOverlay: PropTypes.func,
    toolbarExtra: PropTypes.node,
};

export default PdfViewer;
